import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRightCircle, FileText, Check, MailOpen, Mail, Star, Eye } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { DeptChips } from "@/components/DeptPicker";

const moodLabels = { 1: "😟 Rough", 2: "😐 Meh", 3: "🙂 Okay", 4: "😀 Good", 5: "🚀 Great" };
const roleLabel = { user: "User", manager: "Manager", super_manager: "Super Manager", super_admin: "Super Admin" };
const STATUS_BADGE = {
  off: { label: "Off", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/30" },
  leave: { label: "Leave", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30" },
};

const planText = (it) => (typeof it === "string" ? it : it?.text ?? "");
const planDone = (it) => typeof it === "object" && it?.done;
const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function ReportCard({ report, showUser = false, index = 0, onToggleRead, onAppreciate }) {
  const plan = report.plan_tomorrow || [];
  const tasks = report.tasks_done || [];
  const isRead = report.is_read;
  const statusBadge = STATUS_BADGE[report.status];
  const appreciations = report.appreciations || [];
  const appreciatedByMe = report.appreciated_by_me;
  const readByNames = report.read_by_names || [];
  return (
    <Card
      className={`p-5 sm:p-6 rounded-xl shadow-sm animate-fade-up transition-colors ${isRead ? "border-border opacity-80" : "border-l-4 border-l-primary border-y-border border-r-border"}`}
      style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}
      data-testid="report-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {showUser && (
            <Avatar src={report.user_avatar} name={report.user_name} className="h-10 w-10 text-sm" testid={`report-avatar-${report.user_id}`} />
          )}
          <div>
            {showUser && (
              <p className="font-heading font-semibold leading-tight">
                {report.user_name}
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">{roleLabel[report.role] || "User"}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground font-mono flex flex-wrap items-center gap-2">
              {report.date}
              <DeptChips list={report.departments} testid={`report-depts-${report.id}`} />
              {report.is_draft && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30" data-testid="report-draft-badge">
                  Draft
                </span>
              )}
              {statusBadge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusBadge.cls}`} data-testid={`report-status-${report.status}`}>
                  {statusBadge.label}
                </span>
              )}
              {report.submitted_at && !report.is_draft && (
                <span className="text-[11px] text-muted-foreground normal-case" data-testid="report-published-at">
                  · Published {fmtTs(report.submitted_at)}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {report.mood && <span className="text-xs text-muted-foreground">{moodLabels[report.mood]}</span>}
          {onAppreciate && (
            <Button
              variant={appreciatedByMe ? "default" : "secondary"}
              size="sm"
              className={`rounded-lg h-8 ${appreciatedByMe ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
              onClick={() => onAppreciate(report)}
              data-testid={`appreciate-${report.id}`}
              title="Appreciate exceptional work"
            >
              <Star className={`h-3.5 w-3.5 mr-1 ${appreciatedByMe ? "fill-white" : ""}`} /> {appreciatedByMe ? "Appreciated" : "Appreciate"}
            </Button>
          )}
          {onToggleRead && (
            <Button
              variant={isRead ? "ghost" : "secondary"}
              size="sm"
              className="rounded-lg h-8"
              onClick={() => onToggleRead(report)}
              data-testid={`toggle-read-${report.id}`}
            >
              {isRead ? <><MailOpen className="h-3.5 w-3.5 mr-1" /> Read</> : <><Mail className="h-3.5 w-3.5 mr-1" /> Mark read</>}
            </Button>
          )}
        </div>
      </div>

      {/* Read receipt + appreciation (shown to everyone incl. the member) */}
      {(!report.is_draft && report.status === "report") && (readByNames.length > 0 || appreciations.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`report-meta-${report.id}`}>
          {readByNames.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 bg-emerald-500/10 rounded-full px-2 py-0.5" data-testid={`read-receipt-${report.id}`}>
              <Eye className="h-3 w-3" /> Read by {readByNames.slice(0, 3).join(", ")}{readByNames.length > 3 ? ` +${readByNames.length - 3}` : ""}
            </span>
          )}
          {appreciations.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-500/15 rounded-full px-2 py-0.5" title={appreciations.map((a) => a.by_name).join(", ")} data-testid={`appreciation-badge-${report.id}`}>
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Appreciated by {appreciations.slice(0, 3).map((a) => a.by_name).join(", ")}{appreciations.length > 3 ? ` +${appreciations.length - 3}` : ""}
            </span>
          )}
        </div>
      )}

      {report.summary && (
        <p className="text-sm leading-relaxed mb-4 text-foreground/90 whitespace-pre-wrap break-words">{report.summary}</p>
      )}

      {report.status === "leave" ? (
        <p className="text-sm text-muted-foreground italic">On leave — no report for this day.</p>
      ) : (
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold uppercase tracking-wider">Tasks done</span>
          </div>
          {tasks.length > 0 ? (
            <ul className="space-y-1">
              {tasks.map((it, i) => (
                <li key={i} className="text-sm text-foreground/80 flex gap-1.5">
                  <span className="text-muted-foreground">·</span>
                  <span className="break-words whitespace-pre-wrap">{it}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground/60 italic flex items-center gap-1"><FileText className="h-3 w-3" /> None</p>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-primary">
            <ArrowRightCircle className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold uppercase tracking-wider">Plan tomorrow</span>
          </div>
          {plan.length > 0 ? (
            <ul className="space-y-1">
              {plan.map((it, i) => (
                <li key={i} className="text-sm flex items-start gap-1.5">
                  <span className={`mt-0.5 h-4 w-4 rounded border shrink-0 flex items-center justify-center ${planDone(it) ? "bg-primary border-primary text-white" : "border-border"}`}>
                    {planDone(it) && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`break-words whitespace-pre-wrap ${planDone(it) ? "line-through text-muted-foreground" : "text-foreground/80"}`}>{planText(it)}</span>
                    {typeof it === "object" && it?.origin_date && it.origin_date !== report.date && (
                      <span className="ml-1.5 text-[10px] font-medium text-amber-700 bg-amber-500/15 rounded-full px-1.5 py-0.5">since {it.origin_date}</span>
                    )}
                    {typeof it === "object" && it?.remark && (
                      <span className="block text-[11px] text-muted-foreground italic mt-0.5">↳ {it.remark}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground/60 italic flex items-center gap-1"><FileText className="h-3 w-3" /> None</p>
          )}
        </div>
      </div>
      )}
    </Card>
  );
}
